import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Pagination,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import { ArrowBack, Add, FilterList } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Review, Product, ReviewsResponse } from '../types';
import { reviewsAPI, productsAPI } from '../services/api';
import ReviewCard from '../components/ReviewCard';
import RatingDisplay from '../components/RatingDisplay';

const ProductReviewsPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsData, setReviewsData] = useState<ReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('date_desc');

  useEffect(() => {
    if (productId) {
      loadProductAndReviews();
    }
  }, [productId, page, sort]);

  const loadProductAndReviews = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      
      // Загружаем продукт и отзывы параллельно
      const [productData, reviewsData] = await Promise.all([
        productsAPI.getAll().then(products => products.find(p => p.id === productId)),
        reviewsAPI.getByProduct(productId, page, sort)
      ]);

      setProduct(productData || null);
      setReviews(reviewsData.data);
      setReviewsData(reviewsData);
    } catch (err: any) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSort(event.target.value);
    setPage(1);
  };

  const getRatingPercentage = (rating: number) => {
    if (!reviewsData?.meta.rating_stats) return 0;
    const total = reviewsData.meta.total_reviews;
    const count = reviewsData.meta.rating_stats[rating.toString()] || 0;
    return total > 0 ? (count / total) * 100 : 0;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Продукт не найден</Alert>
          <Button onClick={() => navigate('/products')} sx={{ mt: 2 }}>
            Вернуться к продуктам
          </Button>
        </Box>
      </Container>
    );
  }

  const totalPages = Math.ceil((reviewsData?.meta.total_reviews || 0) / 10);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/products')}
            sx={{ mr: 2 }}
          >
            Назад
          </Button>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Отзывы о "{product.name}"
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/products/${productId}/add-review`)}
          >
            Добавить отзыв
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          {/* Rating Summary */}
          <Box sx={{ flex: '0 0 300px' }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Рейтинг продукта
                </Typography>
                
                {reviewsData && reviewsData.meta.total_reviews > 0 ? (
                  <>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h3" component="div" color="primary.main">
                        {reviewsData.meta.average_rating.toFixed(1)}
                      </Typography>
                      <RatingDisplay 
                        rating={reviewsData.meta.average_rating} 
                        reviewCount={reviewsData.meta.total_reviews}
                      />
                    </Box>

                    <Box>
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: 20 }}>
                            {rating}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={getRatingPercentage(rating)}
                            sx={{ flexGrow: 1, mx: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                            {reviewsData.meta.rating_stats[rating.toString()] || 0}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    Пока нет отзывов
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Reviews List */}
          <Box sx={{ flex: 1 }}>
            {/* Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Отзывы ({reviewsData?.meta.total_reviews || 0})
              </Typography>
              <TextField
                select
                label="Сортировка"
                value={sort}
                onChange={handleSortChange}
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: <FilterList sx={{ mr: 1 }} />,
                }}
              >
                <MenuItem value="date_desc">Сначала новые</MenuItem>
                <MenuItem value="date_asc">Сначала старые</MenuItem>
                <MenuItem value="rating_desc">Высокий рейтинг</MenuItem>
                <MenuItem value="rating_asc">Низкий рейтинг</MenuItem>
              </TextField>
            </Box>

            {/* Reviews */}
            {reviews.length > 0 ? (
              <>
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Отзывы не найдены
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Станьте первым, кто оставит отзыв о данном продукте
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/products/${productId}/add-review`)}
                  sx={{ mt: 2 }}
                >
                  Написать отзыв
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ProductReviewsPage;
